import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { useCreateCustomer } from "@/shared/api/generated/customers";

function useFormSchema() {
  const { t } = useLingui();

  return z.object({
    name: z.string().min(1, { message: t`Velg navn` }),
    powerofficeCustomerId: z.string().optional(),
  });
}

type FormInitial = z.input<ReturnType<typeof useFormSchema>>;
type FormSubmit = z.output<ReturnType<typeof useFormSchema>>;

export default function CreateCustomer({ museumId }: { museumId?: string }) {
  const [opened, { open, close }] = useDisclosure(false);

  const { t } = useLingui();

  const formSchema = useFormSchema();

  const form = useForm<FormInitial, (v: FormInitial) => FormSubmit>({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      powerofficeCustomerId: "",
    },
    validate: zodResolver(formSchema),
  });

  const { mutateAsync: createCustomer } = useCreateCustomer();

  const navigate = useNavigate();

  async function handleSubmit(values: FormSubmit) {
    const { data } = await createCustomer({
      data: {
        name: values.name,
        powerofficeCustomerId: values.powerofficeCustomerId,
      },
    });
    await navigate({
      to: museumId
        ? "/museum/$museumId/customers/$customerId"
        : "/admin/customers/$customerId",
      params: { customerId: data.id.toString(), museumId: museumId },
    });
  }

  return (
    <>
      <Button onClick={open} leftSection={<IconPlus />}>
        <Trans>Opprett kunde</Trans>
      </Button>

      <Modal
        opened={opened}
        onClose={close}
        size="lg"
        title={
          <Title size="sm">
            <Trans>Opprett kunde</Trans>
          </Title>
        }
        transitionProps={{
          onExited: () => form.reset(),
        }}
      >
        <Text size="sm" c="dimmed" mb={8}>
          <Trans>
            Fyll ut den informasjonen du har tilgjengelig. Alle felter kan
            endres i ettertid.
          </Trans>
        </Text>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label={t`Kundenavn`}
              placeholder={t`Kundenavn`}
              withAsterisk
              {...form.getInputProps("name")}
            />

            <TextInput
              label={t`PowerOfficeID`}
              placeholder={t`PowerOfficeID`}
              {...form.getInputProps("powerofficeCustomerId")}
            />

            <Group justify="end" gap={8} mt={8}>
              <Button variant="subtle" onClick={close}>
                <Trans>Avbryt</Trans>
              </Button>
              <Button type="submit">
                <Trans>Opprett</Trans>
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
